import React, { useState, useCallback } from "react";
import { Upload, Phone, Tablet, PenLine, BookOpen, Shirt, HardHat, Usb, Gift, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GoogleS } from "@/components/BrandLogo";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const PRODUCTS = [
  { name: "T-Shirt Total Black", icon: Shirt, type: "tshirt", desc: "Cotone 100% nero. NickLogo S.com + payoff sul cuore, schedabusiness.com al centro petto, QR code sulla schiena.", gift: true },
  { name: "Penna Total Black", icon: PenLine, type: "pen", desc: "Alluminio nero opaco, incisione laser schedabusiness.com, refill G2" },
  { name: "Cover Smartphone", icon: Phone, type: "phone", desc: "TPU nero soft-touch, stampa UV resistente, iPhone/Samsung" },
  { name: "Cover Tablet", icon: Tablet, type: "tablet", desc: "Silicone nero, compatibile iPad e Samsung Tab" },
  { name: "Cappello Total Black", icon: HardHat, type: "cappello", desc: "Cappello baseball nero, ricamo frontale schedabusiness.com a colori." },
  { name: "Tazzina Caffe Nera", icon: "coffee", type: "tazzina", desc: "Ceramica nera opaca, espresso 80ml. Logo inciso laser." },
  { name: "Tazza Tisana/Te", icon: "mug", type: "tazza", desc: "Ceramica nera 350ml, manico ergonomico. Logo + payoff." },
  { name: "Bicchiere Nero", icon: "glass", type: "bicchiere", desc: "Vetro nero satinato 300ml. Logo inciso sabbiatura." },
  { name: "Accendino Total Black", icon: "flame", type: "accendino", desc: "Metallo nero ricaricabile, incisione laser schedabusiness.com" },
  { name: "Cavatappi Premium", icon: "wine", type: "cavatappi", desc: "Acciaio nero, apribottiglie sommelier. Logo inciso laser." },
  { name: "Posacenere Ceramica", icon: "ashtray", type: "posacenere", desc: "Ceramica nera opaca, design minimalista. Logo centrale." },
  { name: "Bussola Tascabile", icon: "compass", type: "bussola", desc: "Metallo nero satinato, bussola funzionante. 'Trova la giusta direzione.'" },
  { name: "Penna USB 32GB", icon: Usb, type: "usb", desc: "USB 3.0 32GB nero opaco, precaricata con guida Google Business Profile." },
  { name: "Block Notes + Penna", icon: BookOpen, type: "notebook", desc: "Set A5 nero soft-touch + penna coordinata. 120 pagine." },
];

const LogoText = ({ size = "sm" }) => {
  const cls = size === "xs" ? "text-[8px]" : size === "sm" ? "text-[11px]" : "text-sm";
  return (
    <span className={`${cls} font-black whitespace-nowrap`} style={{ fontFamily:"'Poppins',sans-serif", WebkitTextStroke:'0.5px #000', paintOrder:'stroke fill' }}>
      <span style={{color:'#4285F4'}}>sch</span><span style={{color:'#EA4335'}}>ed</span><span style={{color:'#FBBC04'}}>abu</span><span style={{color:'#4285F4'}}>sin</span><span style={{color:'#34A853'}}>ess</span><span style={{color:'#EA4335'}}>.com</span>
    </span>
  );
};

const TuoLogo = ({ preview, w = 44, h = 26 }) => (
  <div className="bg-white rounded flex items-center justify-center overflow-hidden" style={{ width: w, height: h, minWidth: w }}>
    {preview ? (
      <img src={preview} alt="Tuo Logo" className="w-full h-full object-contain p-0.5" />
    ) : (
      <span className="text-[7px] text-gray-400 font-bold tracking-tight">TUO LOGO</span>
    )}
  </div>
);

function ProductMockup({ type, logoPreview }) {
  const mockups = {
    tshirt: (
      <div className="relative w-full h-52 flex items-center justify-center">
        <div className="relative" style={{ width:'150px', height:'180px' }}>
          {/* Body */}
          <div style={{ position:'absolute', top:'40px', left:'15px', right:'15px', bottom:'0', background:'linear-gradient(180deg, #111 0%, #000 100%)', borderRadius:'0 0 10px 10px' }}></div>
          {/* Shoulders + sleeves */}
          <div style={{ position:'absolute', top:'0', left:'-20px', right:'-20px', height:'55px', background:'linear-gradient(180deg, #151515 0%, #000 100%)', borderRadius:'0 0 30px 30px', clipPath:'polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%)' }}></div>
          {/* Collar */}
          <div style={{ position:'absolute', top:'0', left:'50%', transform:'translateX(-50%)', width:'40px', height:'15px', background:'#000', borderRadius:'0 0 20px 20px', border:'1px solid #222', borderTop:'none' }}></div>
          {/* NickLogo S.com on heart (top-left chest) */}
          <div className="absolute flex flex-col items-center" style={{ top:'48px', left:'22px' }}>
            <GoogleS size={18} />
            <span style={{ fontSize:'4px', color:'#EA4335', fontWeight:900 }}>.com</span>
          </div>
          {/* schedabusiness.com large center */}
          <div className="absolute flex items-center justify-center" style={{ top:'72px', left:'20px', right:'20px', height:'22px' }}>
            <LogoText size="sm" />
          </div>
          {/* Payoff sotto */}
          <div className="absolute text-center" style={{ top:'92px', left:'20px', right:'20px' }}>
            <span style={{ fontSize:'4.5px', fontWeight:900, color:'white', fontStyle:'italic', fontFamily:"'Poppins',sans-serif" }}>se non ti trovano, non esisti</span>
          </div>
          {/* Client logo on heart right */}
          <div className="absolute" style={{ top:'48px', right:'22px' }}>
            <TuoLogo preview={logoPreview} w={28} h={16} />
          </div>
          {/* QR badge back */}
          <div className="absolute flex items-center justify-center" style={{ bottom:'8px', left:'50%', transform:'translateX(-50)', width:'24px', height:'24px', background:'white', borderRadius:'3px' }}>
            <span style={{ fontSize:'3px', fontWeight:900, color:'#000', textAlign:'center', lineHeight:1 }}>QR</span>
          </div>
          {/* IN REGALO badge */}
          <div className="absolute -top-2 -right-4 bg-[#34A853] text-white text-[6px] font-black px-1.5 py-0.5 rounded-full transform rotate-12">IN REGALO</div>
        </div>
      </div>
    ),
    cappello: (
      <div className="relative w-full h-40 flex items-center justify-center">
        <div className="relative" style={{ width:'140px', height:'90px' }}>
          {/* Brim */}
          <div style={{ position:'absolute', bottom:'0', left:'-10px', right:'-10px', height:'15px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'0 0 50% 50%' }}></div>
          {/* Crown */}
          <div style={{ position:'absolute', top:'0', left:'10px', right:'10px', bottom:'12px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'50% 50% 0 0' }}>
            <div className="absolute inset-0 flex items-center justify-center pt-2">
              <LogoText size="xs" />
            </div>
          </div>
        </div>
      </div>
    ),
    bamboo: (
      <div className="relative w-full h-40 flex items-center justify-center">
        <div className="relative" style={{ width:'100px', height:'110px' }}>
          {/* Bamboo stand */}
          <div style={{ position:'absolute', bottom:'0', left:'10px', right:'10px', height:'70px', background:'linear-gradient(135deg, #c49a6c 0%, #a67c52 50%, #8b6536 100%)', borderRadius:'8px 8px 3px 3px' }}>
            <div className="absolute" style={{ top:'8px', left:'50%', transform:'translateX(-50%)', width:'60%', height:'3px', background:'rgba(0,0,0,0.15)', borderRadius:'2px' }}></div>
            <div className="absolute inset-x-3 bottom-3 flex flex-col items-center justify-center gap-1">
              <LogoText size="xs" />
              <TuoLogo preview={logoPreview} w={28} h={16} />
            </div>
          </div>
          {/* Phone slot */}
          <div style={{ position:'absolute', top:'0', left:'20px', right:'20px', height:'50px', background:'#111', borderRadius:'4px', border:'1px solid #333' }}></div>
        </div>
      </div>
    ),
    usb: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div className="relative" style={{ width:'120px', height:'35px' }}>
          {/* USB body */}
          <div style={{ background:'linear-gradient(180deg, #222 0%, #000 100%)', borderRadius:'5px', width:'100%', height:'100%', boxShadow:'0 4px 12px rgba(0,0,0,0.6)' }}>
            <div className="absolute inset-0 flex items-center justify-center gap-2">
              <LogoText size="xs" />
            </div>
          </div>
          {/* USB connector */}
          <div style={{ position:'absolute', right:'-15px', top:'50%', transform:'translateY(-50%)', width:'18px', height:'14px', background:'linear-gradient(90deg, #888 0%, #aaa 100%)', borderRadius:'0 2px 2px 0' }}></div>
        </div>
      </div>
    ),
    // Generic mockup for new items
    tazzina: (
      <div className="relative w-full h-40 flex items-center justify-center">
        <div className="relative">
          {/* Cup */}
          <div style={{ width:'60px', height:'50px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'3px 3px 8px 8px', position:'relative' }}>
            <div className="absolute inset-0 flex items-center justify-center"><LogoText size="xs" /></div>
          </div>
          {/* Handle */}
          <div style={{ position:'absolute', right:'-14px', top:'10px', width:'14px', height:'28px', border:'3px solid #222', borderLeft:'none', borderRadius:'0 10px 10px 0' }}></div>
          {/* Saucer */}
          <div style={{ width:'80px', height:'8px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'50%', marginTop:'-2px', marginLeft:'-10px' }}></div>
        </div>
      </div>
    ),
    tazza: (
      <div className="relative w-full h-40 flex items-center justify-center">
        <div className="relative">
          <div style={{ width:'70px', height:'70px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'3px 3px 10px 10px', position:'relative' }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
              <LogoText size="xs" />
              <span style={{ fontSize:'4px', color:'white', fontWeight:900, fontStyle:'italic' }}>se non ti trovano...</span>
            </div>
          </div>
          <div style={{ position:'absolute', right:'-16px', top:'12px', width:'16px', height:'36px', border:'3px solid #222', borderLeft:'none', borderRadius:'0 12px 12px 0' }}></div>
        </div>
      </div>
    ),
    bicchiere: (
      <div className="relative w-full h-40 flex items-center justify-center">
        <div style={{ width:'50px', height:'80px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'3px 3px 5px 5px', position:'relative', clipPath:'polygon(5% 0%, 95% 0%, 100% 100%, 0% 100%)' }}>
          <div className="absolute inset-0 flex items-center justify-center"><LogoText size="xs" /></div>
        </div>
      </div>
    ),
    accendino: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div style={{ width:'35px', height:'70px', background:'linear-gradient(180deg, #222 0%, #000 100%)', borderRadius:'5px', position:'relative', boxShadow:'0 4px 12px rgba(0,0,0,0.5)' }}>
          <div style={{ position:'absolute', top:'-3px', left:'50%', transform:'translateX(-50%)', width:'12px', height:'8px', background:'#444', borderRadius:'3px 3px 0 0' }}></div>
          <div className="absolute inset-0 flex items-center justify-center"><GoogleS size={16} /></div>
        </div>
      </div>
    ),
    cavatappi: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div style={{ width:'30px', height:'80px', background:'linear-gradient(180deg, #222 0%, #000 100%)', borderRadius:'4px', position:'relative' }}>
          <div style={{ position:'absolute', top:'0', left:'50%', transform:'translateX(-50%)', width:'40px', height:'12px', background:'#333', borderRadius:'6px' }}></div>
          <div className="absolute inset-0 flex items-center justify-center pt-3"><GoogleS size={14} /></div>
        </div>
      </div>
    ),
    posacenere: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div style={{ width:'90px', height:'25px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'50%', position:'relative' }}>
          <div style={{ position:'absolute', top:'-15px', left:'10px', right:'10px', height:'20px', background:'linear-gradient(180deg, #1a1a1a 0%, #000 100%)', borderRadius:'5px 5px 0 0' }}>
            <div className="absolute inset-0 flex items-center justify-center"><LogoText size="xs" /></div>
          </div>
        </div>
      </div>
    ),
    bussola: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div style={{ width:'60px', height:'60px', background:'linear-gradient(135deg, #1a1a1a 0%, #000 100%)', borderRadius:'50%', border:'2px solid #333', position:'relative' }}>
          <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%) rotate(-15deg)', width:'2px', height:'30px', background:'linear-gradient(180deg, #EA4335 0%, #EA4335 50%, #fff 50%, #fff 100%)' }}></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div style={{ width:'6px', height:'6px', background:'#333', borderRadius:'50%' }}></div>
          </div>
        </div>
      </div>
    ),
    pen: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div className="relative" style={{ width:'220px', height:'30px', background:'linear-gradient(180deg, #222 0%, #000 45%, #111 100%)', borderRadius:'15px', boxShadow:'0 6px 18px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(255,255,255,0.03)' }}>
          <div className="absolute" style={{ right:'10px', top:'-10px', width:'4px', height:'50px', background:'linear-gradient(90deg, #444 0%, #888 50%, #444 100%)', borderRadius:'2px' }}></div>
          <div className="absolute inset-0 flex items-center justify-center gap-3">
            <LogoText size="sm" />
            <TuoLogo preview={logoPreview} />
          </div>
          <div className="absolute" style={{ left:'-14px', top:'50%', transform:'translateY(-50%)', width:'18px', height:'10px', background:'linear-gradient(90deg, #888 0%, #444 100%)', borderRadius:'3px 0 0 3px' }}></div>
        </div>
      </div>
    ),
    phone: (
      <div className="relative w-full h-44 flex items-center justify-center">
        <div className="relative" style={{ width:'100px', height:'175px', background:'linear-gradient(155deg, #1a1a1a 0%, #000 55%, #0d0d0d 100%)', borderRadius:'18px', boxShadow:'0 10px 24px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.06)' }}>
          <div className="absolute" style={{ top:'10px', left:'10px', width:'24px', height:'24px', borderRadius:'50%', background:'radial-gradient(circle at 40% 40%, #333 0%, #111 100%)', border:'1px solid #444' }}></div>
          <div className="absolute" style={{ top:'12px', left:'11px', width:'8px', height:'8px', borderRadius:'50%', background:'radial-gradient(circle at 40% 40%, #222 0%, #000 100%)', border:'1px solid #555' }}></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pt-6">
            <LogoText size="sm" />
            <TuoLogo preview={logoPreview} w={52} h={32} />
          </div>
        </div>
      </div>
    ),
    tablet: (
      <div className="relative w-full h-40 flex items-center justify-center">
        <div className="relative" style={{ width:'160px', height:'115px', background:'linear-gradient(155deg, #1a1a1a 0%, #000 55%, #0d0d0d 100%)', borderRadius:'14px', boxShadow:'0 10px 24px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.06)' }}>
          <div className="absolute" style={{ top:'6px', left:'50%', transform:'translateX(-50%)', width:'8px', height:'8px', borderRadius:'50%', background:'#1a1a1a', border:'1px solid #333' }}></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <LogoText size="md" />
            <TuoLogo preview={logoPreview} w={56} h={34} />
          </div>
        </div>
      </div>
    ),
    notebook: (
      <div className="relative w-full h-44 flex items-center justify-center">
        <div className="relative" style={{ width:'120px', height:'155px', background:'linear-gradient(155deg, #1a1a1a 0%, #000 55%, #0d0d0d 100%)', borderRadius:'3px 10px 10px 3px', boxShadow:'0 10px 24px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          <div className="absolute left-0 top-0 bottom-0 w-3" style={{ background:'linear-gradient(90deg, #333 0%, #1a1a1a 100%)', borderRadius:'3px 0 0 3px' }}></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pl-3">
            <LogoText size="sm" />
            <TuoLogo preview={logoPreview} w={52} h={32} />
          </div>
          <div className="absolute right-3 top-4 bottom-4 flex flex-col justify-between opacity-10">
            {[...Array(12)].map((_,i) => <div key={i} className="w-8 h-px bg-white"></div>)}
          </div>
        </div>
      </div>
    ),
    mousepad: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div className="relative" style={{ width:'200px', height:'110px', background:'linear-gradient(155deg, #1a1a1a 0%, #000 55%, #0d0d0d 100%)', borderRadius:'10px', boxShadow:'0 6px 18px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)' }}>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <LogoText size="md" />
            <TuoLogo preview={logoPreview} w={56} h={34} />
          </div>
        </div>
      </div>
    ),
    keychain: (
      <div className="relative w-full h-36 flex items-center justify-center">
        <div className="relative" style={{ width:'65px', height:'95px' }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2" style={{ width:'22px', height:'22px', border:'3px solid #666', borderRadius:'50%' }}></div>
          <div className="relative" style={{ marginTop:'18px', width:'65px', height:'72px', background:'linear-gradient(155deg, #1a1a1a 0%, #000 55%, #0d0d0d 100%)', borderRadius:'8px', boxShadow:'0 6px 14px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)' }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
              <LogoText size="xs" />
              <TuoLogo preview={logoPreview} w={32} h={18} />
            </div>
          </div>
        </div>
      </div>
    ),
  };
  return mockups[type] || mockups.pen;
}

export default function MerchPage() {
  const [logoPreview, setLogoPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  const handleFile = useCallback((file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => { setLogoPreview(e.target.result); setUploaded(false); };
      reader.readAsDataURL(file);
      const formData = new FormData();
      formData.append('logo', file);
      fetch(`${API_BASE}/api/upload-logo`, { method: 'POST', body: formData })
        .then(r => r.json())
        .then(() => setUploaded(true))
        .catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0c1220]">
      {/* Hero */}
      <div className="pt-12 pb-6 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#4285F4]/15 text-[#4285F4] px-4 py-1.5 rounded-full font-bold text-xs mb-6">
            <Gift className="w-4 h-4" /> INCLUSO NEI PACCHETTI
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4" style={{ fontFamily:"'Poppins',sans-serif" }} data-testid="merch-title">
            Merchandising{" "}
            <span style={{ WebkitTextStroke:'1.5px #000', paintOrder:'stroke fill', textShadow:'1px 1px 0 #000, -1px -1px 0 #000' }}>
              <span className="text-[#4285F4]">sch</span><span className="text-[#EA4335]">ed</span><span className="text-[#FBBC04]">abu</span><span className="text-[#4285F4]">sin</span><span className="text-[#34A853]">ess</span><span className="text-[#EA4335]">.com</span>
            </span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
            Ogni prodotto e' <strong className="text-white">total black</strong> con il nostro logo a colori.
            Lo spazio bianco e' riservato al <strong className="text-white">TUO logo aziendale</strong>.
            <br />Incluso come regalo nei pacchetti Start, Plus e Rooftop.
          </p>
        </div>
      </div>

      {/* Drag & Drop Logo Upload */}
      <div className="max-w-5xl mx-auto px-4 mb-10">
        <div
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${dragOver ? 'border-[#4285F4] bg-[#4285F4]/10 scale-[1.01]' : 'border-white/20 bg-white/[0.03] hover:border-white/40'}`}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => document.getElementById('logo-input').click()}
          data-testid="logo-dropzone"
        >
          <input id="logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
          {logoPreview ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 bg-white rounded-xl p-2 shadow-lg shadow-white/10">
                <img src={logoPreview} alt="Tuo Logo" className="w-full h-full object-contain" />
              </div>
              <p className="text-white font-bold">{uploaded ? "Logo caricato e salvato" : "Caricamento..."}</p>
              <p className="text-gray-500 text-sm">Clicca o trascina per cambiare</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
                <Upload className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-white font-bold text-lg">Trascina qui il TUO logo</p>
              <p className="text-gray-500 text-sm">oppure clicca per selezionare un file (PNG, JPG, SVG)</p>
              <p className="text-gray-600 text-xs mt-1">Esempio: il tuo badge certificato, il logo della tua attivita', il tuo marchio...</p>
            </div>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.map(p => {
            const IconEl = typeof p.icon === 'string' ? null : p.icon;
            return (
            <div key={p.name} className="relative bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden hover:border-white/20 transition-all group" data-testid={`product-${p.type}`}>
              {p.gift && <div className="absolute top-3 right-3 z-10 bg-[#34A853] text-white text-[10px] font-black px-2 py-1 rounded-full">IN REGALO con Plus/Rooftop</div>}
              <div className="bg-black/40 p-4 group-hover:bg-black/30 transition-colors">
                <ProductMockup type={p.type} logoPreview={logoPreview} />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  {IconEl ? <IconEl className="w-5 h-5 text-white/60" /> : <span className="w-5 h-5 text-white/60 text-center text-sm">*</span>}
                  <span className="text-white font-bold text-base">{p.name}</span>
                </div>
                <p className="text-gray-500 text-sm mb-3">{p.desc}</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#34A853]" />
                  <span className="text-[#34A853] text-xs font-bold">Incluso nei pacchetti</span>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 md:p-10 text-center max-w-xl mx-auto">
          <Mail className="w-8 h-8 text-[#4285F4] mx-auto mb-3" />
          <h3 className="text-white text-xl font-black mb-2">Vuoi personalizzare?</h3>
          <p className="text-gray-400 text-sm mb-5 leading-relaxed">
            1000 penne con il TUO logo? Cover per tutto il team?
            Contattaci per un preventivo personalizzato.
          </p>
          <Button
            className="bg-[#4285F4] hover:bg-[#3574d4] text-white rounded-full px-8 py-3 font-bold text-sm"
            onClick={() => window.open('https://wa.me/393420380578?text=Ciao%20Riccardo!%20Vorrei%20info%20sul%20merchandising%20personalizzato', '_blank')}
            data-testid="contact-merch-btn"
          >
            <Mail className="w-4 h-4 mr-2" /> Contattaci
          </Button>
        </div>
      </div>
    </div>
  );
}
